/**
 * ARB JS bootstrap: one message-bundle manager for the whole app.
 *
 * Compiled packs come from `arb-tools compile` (npm script `arb-tools`, wired into the `prebuild` /
 * `prebuild-dev` / `pretest` / `preserver` / `precheck-types` hooks) and are served as static
 * assets under {@link TRANSLATIONS_URL_PREFIX} rather than bundled, so adding a locale does not
 * grow the JS bundle.
 */
import {
  createAsyncMessageBundleManager,
  LocalizationContext,
  LocalizationContextBuilder,
} from '@amzn/arb-tools';
import { DEFAULT_LOCALE } from 'src/i18n/locales';
import { getStoredLocale } from 'src/i18n/localePreference';

// Generated, so gitignored under build/ and absent on a clean checkout; the `pre*` hooks compile it
// before tsc, webpack and jest.
import arbManifest from '../../build/i18n/arbManifest.json';

/** Bundle name for `src/i18n/strings.puff.json`. ARB derives this from the resource file's
 * basename, not its path, so renaming that file renames the bundle and breaks every lookup. */
export const DEFAULT_BUNDLE = 'strings';

/** Must stay in step with `TRANSLATIONS_PUBLISH_DIR` in `configuration/configUtils.js`, where the
 * build copies the packs to; drift is caught by `test/unit-tests/i18n/translationsPublishPath.test.ts`. */
export const TRANSLATIONS_URL_PREFIX = '/i18n';

/** A fresh builder per call is required, not stylistic: `LocalizationContextBuilder` is mutable and
 * its `with*` methods return `this`, so a shared instance would leak one caller's locale into the
 * next. */
export const buildLocalizationContext = (locale: string): LocalizationContext =>
  new LocalizationContextBuilder()
    .withDefaultLocale(DEFAULT_LOCALE)
    .withLocale(locale)
    .build();

/** Synchronous, so the locale is known before first render and there is no flash of English — a
 * server-backed preference could not preserve that (see the seam note in `localePreference.ts`). */
export const resolveInitialLocale = (): string =>
  getStoredLocale() ?? DEFAULT_LOCALE;

export const initialLocalizationContext = (): LocalizationContext =>
  buildLocalizationContext(resolveInitialLocale());

export const mbm = createAsyncMessageBundleManager({
  arbManifest,
  // Only consulted for direct `mbm` use outside the provider tree: `DefaultBundleProvider` always
  // passes the React localization context explicitly.
  localizationContextProvider: initialLocalizationContext,
  urlPrefix: TRANSLATIONS_URL_PREFIX,
});
